/**
 * Vanguard permanent talent tree — data only.
 * Source: VANGUARD_FINAL_SPEC.md. Do not invent nodes or balance values.
 * Combat application of effects is out of scope for M6-A.
 */

export type VanguardBranchId =
  | 'general'
  | 'berserker'
  | 'skirmisher'
  | 'bulwark';

export type TalentTier = 1 | 2 | 3;

export type TalentNodeKind = 'passive' | 'active';

/**
 * Effect metadata for UI / future combat wiring.
 * Values match VANGUARD_FINAL_SPEC per-rank amounts; units are as named.
 */
export type TalentEffectKind =
  | 'max_hp_pct'
  | 'armour_pct'
  | 'attack_pct'
  | 'attack_damage_pct'
  | 'crit_chance_pp'
  | 'crit_damage_pct'
  | 'bleed_chance_pp'
  | 'bleed_damage_pct'
  | 'damage_vs_below_50_hp_pct'
  | 'damage_vs_below_25_hp_pct'
  | 'damage_vs_bleeding_pct'
  | 'two_handed_weapon_damage_pct'
  | 'two_handed_affix_stats_pct'
  | 'dual_wield_damage_pct'
  | 'off_hand_dagger_damage_pct'
  | 'off_hand_affix_stats_pct'
  | 'counterattack_chance_pp'
  | 'crit_chance_on_consecutive_hits_pp'
  | 'chance_to_attack_again_pp'
  | 'block_chance_pp'
  | 'counterattack_chance_when_blocking_pp'
  | 'counterattack_damage_after_blocking_pct'
  | 'unlock_active_skill';

export interface TalentEffectDef {
  kind: TalentEffectKind;
  /** Per-rank magnitude from the final spec (percentage points or percent). */
  perRank: number;
  /**
   * Human-readable effect line for the detail panel.
   * Must stay aligned with VANGUARD_FINAL_SPEC wording.
   */
  description: string;
  /** Linked dungeon active skill id when kind === unlock_active_skill. */
  skillId?: string;
  /** Notes for TBD combat rules — never invent resolution here. */
  openNote?: string;
}

export interface TalentNodeDef {
  id: string;
  name: string;
  branch: VanguardBranchId;
  tier: TalentTier;
  kind: TalentNodeKind;
  maxRanks: number;
  effect: TalentEffectDef;
  /**
   * Decorative layout position in tree space (implementation decision).
   * Origin is top-left of the pan/zoom canvas; not a gameplay gate.
   */
  layout: { x: number; y: number };
  /** Decorative parent edges (never purchase prerequisites). */
  decorativeParents?: readonly string[];
}

export interface VanguardBranchDef {
  id: VanguardBranchId;
  name: string;
  /** Spec: specs open only after this many points invested in General Vanguard. */
  requiresGeneralPoints?: number;
}

/** Confirmed gates from VANGUARD_FINAL_SPEC. */
export const VANGUARD_TALENT_GATES = {
  /** General T2 unlocks at this many points spent in General Vanguard. */
  generalTier2At: 5,
  /** General T3 unlocks at this many points spent in General Vanguard. */
  generalTier3At: 10,
  /** All three specialisations open simultaneously after this many General points. */
  specialisationsAtGeneralPoints: 15,
  /** Spec T2 unlocks at this many points spent in THAT branch. */
  specTier2At: 5,
  /** Spec T3 unlocks at this many points spent in THAT branch. */
  specTier3At: 12,
  /** Max class talent points from levels (levels 2–60). */
  maxLevelPoints: 59,
  /** Reserved achievement-point budget — not granted until designed. */
  reservedAchievementBudget: 10,
  /** Hard cap on total class talent points once achievements exist. */
  maxTotalClassPoints: 69,
} as const;

export const VANGUARD_BRANCHES: readonly VanguardBranchDef[] = [
  { id: 'general', name: 'General Vanguard' },
  {
    id: 'berserker',
    name: 'Berserker',
    requiresGeneralPoints: VANGUARD_TALENT_GATES.specialisationsAtGeneralPoints,
  },
  {
    id: 'skirmisher',
    name: 'Skirmisher',
    requiresGeneralPoints: VANGUARD_TALENT_GATES.specialisationsAtGeneralPoints,
  },
  {
    id: 'bulwark',
    name: 'Bulwark',
    requiresGeneralPoints: VANGUARD_TALENT_GATES.specialisationsAtGeneralPoints,
  },
] as const;

/**
 * Exact permanent tree from VANGUARD_FINAL_SPEC.
 * Layout positions are implementation decisions for the pan/zoom canvas.
 */
export const VANGUARD_TALENT_NODES: readonly TalentNodeDef[] = [
  // —— General Vanguard T1 ——
  {
    id: 'vg_gen_health',
    name: 'Health',
    branch: 'general',
    tier: 1,
    kind: 'passive',
    maxRanks: 5,
    effect: {
      kind: 'max_hp_pct',
      perRank: 2,
      description: '+2% max HP per rank',
    },
    layout: { x: 80, y: 90 },
  },
  {
    id: 'vg_gen_armour',
    name: 'Armour',
    branch: 'general',
    tier: 1,
    kind: 'passive',
    maxRanks: 5,
    effect: {
      kind: 'armour_pct',
      perRank: 3,
      description: '+3% armour per rank',
    },
    layout: { x: 160, y: 90 },
    decorativeParents: ['vg_gen_health'],
  },
  {
    id: 'vg_gen_attack',
    name: 'Attack',
    branch: 'general',
    tier: 1,
    kind: 'passive',
    maxRanks: 5,
    effect: {
      kind: 'attack_pct',
      perRank: 2,
      description: '+2% attack per rank',
    },
    layout: { x: 240, y: 90 },
    decorativeParents: ['vg_gen_armour'],
  },
  {
    id: 'vg_gen_cleave',
    name: 'Cleave',
    branch: 'general',
    tier: 1,
    kind: 'active',
    maxRanks: 1,
    effect: {
      kind: 'unlock_active_skill',
      perRank: 1,
      skillId: 'cleave',
      description: 'Unlock dungeon active skill: Cleave',
    },
    layout: { x: 320, y: 90 },
    decorativeParents: ['vg_gen_attack'],
  },
  // —— General Vanguard T2 ——
  {
    id: 'vg_gen_crit_chance',
    name: 'Crit Chance',
    branch: 'general',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'crit_chance_pp',
      perRank: 1,
      description: '+1 percentage point crit chance per rank',
    },
    layout: { x: 80, y: 200 },
    decorativeParents: ['vg_gen_health'],
  },
  {
    id: 'vg_gen_crit_damage',
    name: 'Crit Damage',
    branch: 'general',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'crit_damage_pct',
      perRank: 5,
      description: '+5% crit damage per rank',
    },
    layout: { x: 160, y: 200 },
    decorativeParents: ['vg_gen_armour', 'vg_gen_crit_chance'],
  },
  {
    id: 'vg_gen_bleed_chance',
    name: 'Bleed Chance',
    branch: 'general',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'bleed_chance_pp',
      perRank: 2,
      description:
        '+2 percentage points Bleed chance on ALL Vanguard weapon hits per rank',
    },
    layout: { x: 240, y: 200 },
    decorativeParents: ['vg_gen_attack', 'vg_gen_crit_damage'],
  },
  {
    id: 'vg_gen_bloodlust',
    name: 'Bloodlust',
    branch: 'general',
    tier: 2,
    kind: 'active',
    maxRanks: 1,
    effect: {
      kind: 'unlock_active_skill',
      perRank: 1,
      skillId: 'bloodlust',
      description: 'Unlock dungeon active skill: Bloodlust',
    },
    layout: { x: 320, y: 200 },
    decorativeParents: ['vg_gen_cleave', 'vg_gen_bleed_chance'],
  },
  // —— General Vanguard T3 ——
  {
    id: 'vg_gen_bleed_damage',
    name: 'Bleed Damage',
    branch: 'general',
    tier: 3,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'bleed_damage_pct',
      perRank: 5,
      description: '+5% Bleed damage per rank',
    },
    layout: { x: 80, y: 310 },
    decorativeParents: ['vg_gen_crit_chance'],
  },
  {
    id: 'vg_gen_dmg_below_50',
    name: 'Damage vs Below 50% HP',
    branch: 'general',
    tier: 3,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'damage_vs_below_50_hp_pct',
      perRank: 3,
      description: '+3% damage vs enemy below 50% HP per rank',
    },
    layout: { x: 160, y: 310 },
    decorativeParents: ['vg_gen_crit_damage', 'vg_gen_bleed_damage'],
  },
  {
    id: 'vg_gen_dmg_bleeding',
    name: 'Damage vs Bleeding',
    branch: 'general',
    tier: 3,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'damage_vs_bleeding_pct',
      perRank: 3,
      description: '+3% damage vs bleeding enemy per rank',
    },
    layout: { x: 240, y: 310 },
    decorativeParents: ['vg_gen_bleed_chance', 'vg_gen_dmg_below_50'],
  },
  {
    id: 'vg_gen_rend',
    name: 'Rend',
    branch: 'general',
    tier: 3,
    kind: 'active',
    maxRanks: 1,
    effect: {
      kind: 'unlock_active_skill',
      perRank: 1,
      skillId: 'rend',
      description: 'Unlock dungeon active skill: Rend',
    },
    layout: { x: 320, y: 310 },
    decorativeParents: ['vg_gen_bloodlust', 'vg_gen_dmg_bleeding'],
  },

  // —— Berserker T1 ——
  {
    id: 'vg_ber_attack_damage',
    name: 'Attack Damage',
    branch: 'berserker',
    tier: 1,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'attack_damage_pct',
      perRank: 2,
      description: '+2% attack damage (generic) per rank',
    },
    layout: { x: 460, y: 90 },
  },
  {
    id: 'vg_ber_two_handed_damage',
    name: 'Two-Handed Weapon Damage',
    branch: 'berserker',
    tier: 1,
    kind: 'passive',
    maxRanks: 5,
    effect: {
      kind: 'two_handed_weapon_damage_pct',
      perRank: 3,
      description: '+3% damage with two-handed weapons per rank',
    },
    layout: { x: 540, y: 90 },
    decorativeParents: ['vg_ber_attack_damage'],
  },
  {
    id: 'vg_ber_shield_breaker',
    name: 'Shield Breaker',
    branch: 'berserker',
    tier: 1,
    kind: 'active',
    maxRanks: 1,
    effect: {
      kind: 'unlock_active_skill',
      perRank: 1,
      skillId: 'shield_breaker',
      description: 'Unlock dungeon active skill: Shield Breaker',
    },
    layout: { x: 620, y: 90 },
    decorativeParents: ['vg_ber_two_handed_damage'],
  },
  // —— Berserker T2 ——
  {
    id: 'vg_ber_two_handed_affix',
    name: 'Two-Handed Affix Stats',
    branch: 'berserker',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'two_handed_affix_stats_pct',
      perRank: 5,
      description: '+5% applicable affix stats on two-handed weapons per rank',
      openNote: 'Affix-stat scaling/eligibility edge cases TBD',
    },
    layout: { x: 460, y: 200 },
    decorativeParents: ['vg_ber_attack_damage'],
  },
  {
    id: 'vg_ber_crit_chance',
    name: 'Crit Chance',
    branch: 'berserker',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'crit_chance_pp',
      perRank: 1,
      description: '+1 percentage point crit chance per rank',
    },
    layout: { x: 540, y: 200 },
    decorativeParents: ['vg_ber_two_handed_damage', 'vg_ber_two_handed_affix'],
  },
  {
    id: 'vg_ber_crit_damage',
    name: 'Crit Damage',
    branch: 'berserker',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'crit_damage_pct',
      perRank: 5,
      description: '+5% crit damage per rank',
    },
    layout: { x: 620, y: 200 },
    decorativeParents: ['vg_ber_shield_breaker', 'vg_ber_crit_chance'],
  },
  // —— Berserker T3 ——
  {
    id: 'vg_ber_dmg_below_25',
    name: 'Damage vs Below 25% HP',
    branch: 'berserker',
    tier: 3,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'damage_vs_below_25_hp_pct',
      perRank: 4,
      description: '+4% damage vs enemy below 25% HP per rank',
    },
    layout: { x: 500, y: 310 },
    decorativeParents: ['vg_ber_two_handed_affix', 'vg_ber_crit_chance'],
  },
  {
    id: 'vg_ber_sunder',
    name: 'Sunder',
    branch: 'berserker',
    tier: 3,
    kind: 'active',
    maxRanks: 1,
    effect: {
      kind: 'unlock_active_skill',
      perRank: 1,
      skillId: 'sunder',
      description: 'Unlock dungeon active skill: Sunder',
    },
    layout: { x: 600, y: 310 },
    decorativeParents: ['vg_ber_crit_damage', 'vg_ber_dmg_below_25'],
  },

  // —— Skirmisher T1 ——
  {
    id: 'vg_ski_counterattack',
    name: 'Counterattack Chance',
    branch: 'skirmisher',
    tier: 1,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'counterattack_chance_pp',
      perRank: 1,
      description: '+1 percentage point counterattack chance per rank',
    },
    layout: { x: 740, y: 90 },
  },
  {
    id: 'vg_ski_dual_wield',
    name: 'Dual-Wield Damage',
    branch: 'skirmisher',
    tier: 1,
    kind: 'passive',
    maxRanks: 5,
    effect: {
      kind: 'dual_wield_damage_pct',
      perRank: 3,
      description: '+3% damage when dual-wielding per rank',
    },
    layout: { x: 820, y: 90 },
    decorativeParents: ['vg_ski_counterattack'],
  },
  {
    id: 'vg_ski_flurry',
    name: 'Flurry',
    branch: 'skirmisher',
    tier: 1,
    kind: 'active',
    maxRanks: 1,
    effect: {
      kind: 'unlock_active_skill',
      perRank: 1,
      skillId: 'flurry',
      description: 'Unlock dungeon active skill: Flurry',
    },
    layout: { x: 900, y: 90 },
    decorativeParents: ['vg_ski_dual_wield'],
  },
  // —— Skirmisher T2 ——
  {
    id: 'vg_ski_offhand_dagger',
    name: 'Off-Hand Dagger Damage',
    branch: 'skirmisher',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'off_hand_dagger_damage_pct',
      perRank: 3,
      description: '+3% when off-hand is a dagger per rank',
    },
    layout: { x: 740, y: 200 },
    decorativeParents: ['vg_ski_counterattack'],
  },
  {
    id: 'vg_ski_offhand_affix',
    name: 'Off-Hand Affix Stats',
    branch: 'skirmisher',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'off_hand_affix_stats_pct',
      perRank: 5,
      description:
        '+5% off-hand affix stats (ANY equipped off-hand, including shield) per rank',
      openNote: 'Affix-stat scaling/eligibility edge cases TBD',
    },
    layout: { x: 820, y: 200 },
    decorativeParents: ['vg_ski_dual_wield', 'vg_ski_offhand_dagger'],
  },
  {
    id: 'vg_ski_crit_consecutive',
    name: 'Crit on Consecutive Hits',
    branch: 'skirmisher',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'crit_chance_on_consecutive_hits_pp',
      perRank: 1,
      description:
        '+1 percentage point crit chance per consecutive critical hit in the current attack sequence, per rank (accumulating; max 3 ranks)',
      openNote:
        'M7-D1B final: CONSECUTIVE_CRIT_STACKING_MODE=accumulate_per_consecutive. Bonus = ranks × streak before the next chained attack rolls; resets on non-crit or sequence end.',
    },
    layout: { x: 900, y: 200 },
    decorativeParents: ['vg_ski_flurry', 'vg_ski_offhand_affix'],
  },
  // —— Skirmisher T3 ——
  {
    id: 'vg_ski_attack_again',
    name: 'Chance to Attack Again',
    branch: 'skirmisher',
    tier: 3,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'chance_to_attack_again_pp',
      perRank: 1,
      description:
        '+1 percentage point chance to attack again; additional attack cannot itself retrigger this passive',
    },
    layout: { x: 780, y: 310 },
    decorativeParents: ['vg_ski_offhand_dagger', 'vg_ski_offhand_affix'],
  },
  {
    id: 'vg_ski_twin_strike',
    name: 'Twin Strike',
    branch: 'skirmisher',
    tier: 3,
    kind: 'active',
    maxRanks: 1,
    effect: {
      kind: 'unlock_active_skill',
      perRank: 1,
      skillId: 'twin_strike',
      description: 'Unlock dungeon active skill: Twin Strike',
    },
    layout: { x: 880, y: 310 },
    decorativeParents: ['vg_ski_crit_consecutive', 'vg_ski_attack_again'],
  },

  // —— Bulwark T1 ——
  {
    id: 'vg_bul_armour',
    name: 'Armour',
    branch: 'bulwark',
    tier: 1,
    kind: 'passive',
    maxRanks: 5,
    effect: {
      kind: 'armour_pct',
      perRank: 3,
      description: '+3% armour per rank',
    },
    layout: { x: 1020, y: 90 },
  },
  {
    id: 'vg_bul_health',
    name: 'Health',
    branch: 'bulwark',
    tier: 1,
    kind: 'passive',
    maxRanks: 5,
    effect: {
      kind: 'max_hp_pct',
      perRank: 2,
      description: '+2% max HP per rank',
    },
    layout: { x: 1100, y: 90 },
    decorativeParents: ['vg_bul_armour'],
  },
  {
    id: 'vg_bul_iron_harvest',
    name: 'Iron Harvest',
    branch: 'bulwark',
    tier: 1,
    kind: 'active',
    maxRanks: 1,
    effect: {
      kind: 'unlock_active_skill',
      perRank: 1,
      skillId: 'iron_harvest',
      description: 'Unlock dungeon active skill: Iron Harvest',
    },
    layout: { x: 1180, y: 90 },
    decorativeParents: ['vg_bul_health'],
  },
  // —— Bulwark T2 ——
  {
    id: 'vg_bul_offhand_affix',
    name: 'Off-Hand Affix Stats',
    branch: 'bulwark',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'off_hand_affix_stats_pct',
      perRank: 5,
      description:
        '+5% affix stats (ANY equipped off-hand, including dagger) per rank',
      openNote: 'Affix-stat scaling/eligibility edge cases TBD',
    },
    layout: { x: 1020, y: 200 },
    decorativeParents: ['vg_bul_armour'],
  },
  {
    id: 'vg_bul_block_chance',
    name: 'Block Chance',
    branch: 'bulwark',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'block_chance_pp',
      perRank: 1,
      description: '+1 percentage point block chance per rank',
    },
    layout: { x: 1100, y: 200 },
    decorativeParents: ['vg_bul_health', 'vg_bul_offhand_affix'],
  },
  {
    id: 'vg_bul_counter_on_block',
    name: 'Counter when Blocking',
    branch: 'bulwark',
    tier: 2,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'counterattack_chance_when_blocking_pp',
      perRank: 2,
      description:
        '+2 percentage points counterattack chance when blocking; stacks with base counter chance',
    },
    layout: { x: 1180, y: 200 },
    decorativeParents: ['vg_bul_iron_harvest', 'vg_bul_block_chance'],
  },
  // —— Bulwark T3 ——
  {
    id: 'vg_bul_counter_damage',
    name: 'Counter Damage after Block',
    branch: 'bulwark',
    tier: 3,
    kind: 'passive',
    maxRanks: 3,
    effect: {
      kind: 'counterattack_damage_after_blocking_pct',
      perRank: 5,
      description: '+5% counterattack damage after blocking per rank',
    },
    layout: { x: 1060, y: 310 },
    decorativeParents: ['vg_bul_offhand_affix', 'vg_bul_block_chance'],
  },
  {
    id: 'vg_bul_kinetic_cascade',
    name: 'Kinetic Cascade',
    branch: 'bulwark',
    tier: 3,
    kind: 'active',
    maxRanks: 1,
    effect: {
      kind: 'unlock_active_skill',
      perRank: 1,
      skillId: 'kinetic_cascade',
      description: 'Unlock dungeon active skill: Kinetic Cascade',
    },
    layout: { x: 1160, y: 310 },
    decorativeParents: ['vg_bul_counter_on_block', 'vg_bul_counter_damage'],
  },
] as const;

const NODE_BY_ID: ReadonlyMap<string, TalentNodeDef> = new Map(
  VANGUARD_TALENT_NODES.map((node) => [node.id, node]),
);

export function getVanguardTalentNode(id: string): TalentNodeDef {
  const node = NODE_BY_ID.get(id);
  if (!node) {
    throw new Error(`Unknown Vanguard talent node: ${id}`);
  }
  return node;
}

export function listVanguardTalentNodes(
  branch?: VanguardBranchId,
): TalentNodeDef[] {
  if (!branch) {
    return [...VANGUARD_TALENT_NODES];
  }
  return VANGUARD_TALENT_NODES.filter((node) => node.branch === branch);
}

export function getVanguardBranch(id: VanguardBranchId): VanguardBranchDef {
  const branch = VANGUARD_BRANCHES.find((b) => b.id === id);
  if (!branch) {
    throw new Error(`Unknown Vanguard branch: ${id}`);
  }
  return branch;
}

/** Spec totals: 95 passive ranks + 9 active unlocks = 104 possible investments. */
export function vanguardTalentInvestmentCapacity(): {
  passiveRanks: number;
  activeUnlocks: number;
  total: number;
} {
  let passiveRanks = 0;
  let activeUnlocks = 0;
  for (const node of VANGUARD_TALENT_NODES) {
    if (node.kind === 'active') {
      activeUnlocks += node.maxRanks;
    } else {
      passiveRanks += node.maxRanks;
    }
  }
  return {
    passiveRanks,
    activeUnlocks,
    total: passiveRanks + activeUnlocks,
  };
}
